import React, {useMemo, useState} from 'react';

import {
    DescriptionAutosuggestActionBar,
} from 'src/modules/event-create/attributes-block/attribute-form/suggest/description-autosuggest-action-bar';
import {FieldName} from 'src/modules/event-create/typedefs';
import {capitalizeFirstLetter} from 'src/modules/event-create/utils/capitalizeFirstLetter';
import {MAX_LENGTH_ATTRIBUTE_DESCRIPTION} from 'src/modules/event-create/validations/attribute-description-validation';
import {
    getAttributeDescriptionSuggestions,
    getAttributeDescriptionSuggestionsSuccess,
} from 'src/openapi/apis/AttributeControllerApi';
import {EventChangeRequest} from 'src/openapi/models/EventChangeRequest';
import {useAppDispatch} from 'src/store';

import {
    Autosuggest,
    AutosuggestItem,
    AutosuggestListCaption,
    AutosuggestNoResults,
} from '@pb/kit-component-autosuggest-v2';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';

type Props = {
    onSuggestSelect: (suggest: string) => void,
    attributeName?: string,
    attributeType?: string,
    eventChangeRequest?: EventChangeRequest,
    disabled: boolean,
    hasError: boolean,
    value: string,
    onBlur: (event: React.FocusEvent<HTMLInputElement>, name?: string) => void,
    onChangeField: (value: string, name?: string, itemsType?: string) => void,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
};

const DESCRIPTION_PLACEHOLDER = 'Description';
const LIST_CAPTION = 'Suggestions';

export const DescriptionAutosuggest = (props: Props) => {
    const {
        attributeName,
        attributeType,
        eventChangeRequest,
        onSuggestSelect,
        disabled,
        hasError,
        value,
        onBlur,
        onChangeField,
        onChange,
    } = props;

    const dispatch = useAppDispatch();
    const [suggests, setSuggests] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);
    const [shown, setShown] = useState(false);

    const onClick = () => {
        if (!attributeName || disabled || shown) {
            return;
        }

        setBusy(true);
        setShown(true);

        dispatch(getAttributeDescriptionSuggestions({name: attributeName})).then(res => {
            if (!res.payload.error && res.type === getAttributeDescriptionSuggestionsSuccess.type) {
                setSuggests(res.payload.data);
            }

            setBusy(false);
        });
    };

    const onInputBlur = (event: React.FocusEvent<HTMLInputElement>, name?: string) => {
        setShown(false);
        onBlur(event, name);
    };

    const onItemSelect = (item: string) => {
        onSuggestSelect(item);
        setShown(false);
    };

    const onDescriptionGenerate = (suggest: string) => {
        onSuggestSelect(suggest);
        onChangeField(suggest, FieldName.DESCRIPTION);
    };

    const items = useMemo(() => suggests.map(item => (
        <AutosuggestItem key={item} value={item}>
            <EllipsisTextHint>
                {item}
            </EllipsisTextHint>
        </AutosuggestItem>
    )), [suggests]);

    return (
        <Autosuggest
            name={FieldName.DESCRIPTION}
            shown={Boolean(shown && !busy)}
            setShown={setShown}
            onChange={onChange}
            onClick={onClick}
            onItemSelect={onItemSelect}
            hideOnItemClick={true}
            onBlur={onInputBlur}
            value={capitalizeFirstLetter(value)}
            placeholder={DESCRIPTION_PLACEHOLDER}
            disabled={disabled}
            maxLength={MAX_LENGTH_ATTRIBUTE_DESCRIPTION}
            type="ABSOLUTE"
            color={hasError ? 'ERROR' : 'DEFAULT'}
            endAdornment={(
                <DescriptionAutosuggestActionBar
                    busy={busy}
                    attributeName={attributeName}
                    attributeType={attributeType}
                    eventChangeRequest={eventChangeRequest}
                    onDescriptionGenerate={onDescriptionGenerate}
                />
            )}>
            <>
                {items.length > 0 ? (
                    <Group direction="v" gap="none">
                        <AutosuggestListCaption>{LIST_CAPTION}</AutosuggestListCaption>
                        {items}
                    </Group>
                ) : <AutosuggestNoResults/>}
            </>
        </Autosuggest>
    );
};
